/* eslint-disable react/jsx-props-no-spreading */
import { environment } from '@core/environment';
import { lngLatToXY } from '@core/logic';
import { SearchOutline } from '@deepup/icons';
import type MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import {
  Autocomplete,
  CircularProgress,
  InputAdornment,
  ListItem,
  ListItemText,
  TextField,
  useTheme,
} from '@mui/material';
import axios from 'axios';
import { throttle } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useConnectScans } from '../menu/ScanOrganization/ScansInScopeList/connect';
import { useConnect } from './connect';

interface Option {
  primaryText: string;
  secondaryText: string;
  center: number[];
}

const toOptions = (searchResults: MapboxGeocoder.Result[]): Option[] =>
  searchResults.map((result) => ({
    primaryText: result.text,
    secondaryText: result.place_name.replace(result.text + ', ', ''),
    center: result.center,
  }));

export const AddressSearchField = () => {
  const { cameraGoto, boundingBox, loadSearchedPhoto } = useConnect();
  const [options, setOptions] = useState<Option[]>([]);
  const theme = useTheme();
  const { loadSearchedScan } = useConnectScans();
  const [isLoading, setIsLoading] = useState(false);

  const fetchResults = async (searchTerm: string) => {
    if (searchTerm === '') {
      setOptions([]);
      return;
    }

    // if search term is a UUID, return empty options
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(searchTerm)) {
      setOptions([]);
      setIsLoading(true);
      await Promise.allSettled([
        loadSearchedScan(searchTerm.trim()),
        loadSearchedPhoto(searchTerm.trim()),
      ]);
      setIsLoading(false);
      return;
    }

    const encodedSearchTerm = encodeURIComponent(searchTerm);

    const urlParams = new URLSearchParams({
      access_token: environment.mapBox.accessToken,
      language: 'de',
      types: 'address',
    });

    if (boundingBox) {
      urlParams.set('bbox', boundingBox.join(','));
    }

    const { data } = await axios.get<{ features: MapboxGeocoder.Result[] }>(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodedSearchTerm}.json?${urlParams}`,
    );

    setOptions(toOptions(data.features));
  };

  // Keep a mutable ref pointing to the latest fetchResults to completely avoid stale closures
  const fetchResultsRef = useRef(fetchResults);
  useEffect(() => {
    fetchResultsRef.current = fetchResults;
  }, [boundingBox, loadSearchedScan, loadSearchedPhoto, cameraGoto]);

  // Completely stable throttled function identity ensures throttle timers are never reset
  const fetchResultsThrottled = useMemo(
    () =>
      throttle(
        (searchTerm: string) => {
          fetchResultsRef.current(searchTerm);
        },
        250,
        { leading: false, trailing: true },
      ),
    [],
  );

  // Clean up pending throttled callbacks on unmount to prevent leaks/setStates
  useEffect(() => {
    return () => {
      fetchResultsThrottled.cancel();
    };
  }, [fetchResultsThrottled]);

  return (
    <Autocomplete
      freeSolo
      id="location-search-box"
      options={options}
      onChange={(_, option, reason) => {
        if (reason !== 'selectOption') return;

        const [lng, lat] = (option as Option).center;
        try {
          const { x, y } = lngLatToXY({ lng, lat });
          cameraGoto({ x, y });
          setOptions([]);
        } catch (_e) {
          return;
        }
      }}
      onInputChange={(_, newText) => {
        fetchResultsThrottled(newText);
      }}
      getOptionLabel={(option) =>
        typeof option === 'object' ? option?.primaryText + ', ' + option?.secondaryText : option
      }
      noOptionsText="No results"
      renderInput={(params) => (
        <TextField
          {...params}
          disabled={isLoading}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                {isLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <SearchOutline fontSize={24} fill={theme.palette.text.primary} />
                )}
              </InputAdornment>
            ),
          }}
        />
      )}
      renderOption={(params, option) => (
        <ListItem {...params} key={`${option.primaryText}-${option.secondaryText}`}>
          <ListItemText primary={option.primaryText} secondary={option.secondaryText} />
        </ListItem>
      )}
      filterOptions={(options) => options}
      sx={(theme) => ({ minWidth: theme.spacing(30) })}
    />
  );
};
